/**
 * `engine/audio` — SOUND PREFERENCES and the MENU/BATTLE MUSIC BED.
 *
 * ── WHAT IS AND IS NOT IN THIS BARREL ───────────────────────────────────────
 * The prefs store and the `AudioManager` are here: both are needed by the shell
 * as soon as it is up (the menu bed starts on the first gesture). The Settings
 * UI — `sound-panel.ts` — is DELIBERATELY NOT RE-EXPORTED: it pulls in
 * `@/engine/i18n` and `engine/layout/dom` and belongs to the settings screen,
 * which imports it by path, so a visitor who never opens Settings does not pay
 * for it. Same rule `engine/a11y/index.ts` states for its own panel.
 *
 * `cues.ts` — the UI hover/tap/purchase cues — is NOT re-exported either, and
 * for a harder reason than cost: it reaches the match mixer through an
 * `await import()`, and re-exporting it here would put that specifier in the
 * static graph of everything that touches this barrel. `main.ts` imports it by
 * path. It also imports THIS file, which is why the arrow only points one way.
 *
 * The MATCH audio (`engine/match/audio/**`) is not here either: it is a separate
 * system (one-shot effects + an adaptive three-stem score) loaded with the match
 * chunk. `bindMusicDirectorToPrefs` / `bindSfxEngineToPrefs` below let those
 * match objects follow the SAME prefs without this shell module importing match
 * code — they take a small structural interface, so nothing pulls the match
 * chunk into the shell graph.
 */
import { audioPrefsStore, type AudioPrefs } from './prefs';

export {
  AUDIO_STORAGE_KEYS,
  AUDIO_VOLUME_FIELDS,
  DEFAULT_AUDIO_PREFS,
  audioPrefs,
  audioPrefsEqual,
  audioPrefsStore,
  clampVolume,
  initAudioPrefs,
  normaliseAudioPrefs,
  resetAudioPrefsForTests,
  setAudioPrefs,
  updateAudioPrefs,
  type AudioPrefs,
  type AudioVolumeField,
} from './prefs';

export {
  AudioManager,
  MUSIC_SOURCES,
  MUSIC_TRACKS,
  audioManager,
  resetAudioManagerForTests,
  type AudioManagerDeps,
  type MediaElementLike,
  type MusicScene,
  type UnlockTargetLike,
} from './manager';

/* ═══════════════════════════════════════════ match-audio prefs binding ══ */

/** What the match's `MusicDirector` exposes for prefs. Structural on purpose. */
export interface MusicPrefsSink {
  applyAudioPrefs(prefs: { music: number; master: number; muted: boolean }): void;
}

/** What the match's `AudioEngine` exposes for prefs. Structural on purpose. */
export interface SfxPrefsSink {
  setMasterGain(level: number): void;
  setMuted(muted: boolean): void;
}

function clamp01(v: number): number {
  return !(v > 0) ? 0 : v > 1 ? 1 : v;
}

/**
 * Subscribe a match `MusicDirector` to the shared sound prefs. Fires once
 * immediately, then on every slider/mute change. Returns an unsubscribe. The
 * match calls this after constructing its director so the score obeys Settings.
 */
export function bindMusicDirectorToPrefs(director: MusicPrefsSink): () => void {
  return audioPrefsStore.subscribe((p: AudioPrefs) => {
    director.applyAudioPrefs({ music: clamp01(p.music), master: clamp01(p.master), muted: p.muted });
  });
}

/**
 * Subscribe a match `AudioEngine` (the SFX mixer) to the shared prefs. The
 * engine's master gain becomes `master * sfx`, and its mute follows the pref.
 */
export function bindSfxEngineToPrefs(engine: SfxPrefsSink): () => void {
  return audioPrefsStore.subscribe((p: AudioPrefs) => {
    engine.setMasterGain(clamp01(p.master) * clamp01(p.sfx));
    engine.setMuted(p.muted);
  });
}

/* ═══════════════════════════════════════════════════════ the UI sound bus ══ */

/**
 * The interface bus (`ui-sounds.ts`) imports nothing but the prefs store, so
 * unlike `cues.ts` it is safe in this barrel: no match chunk rides along.
 */
export {
  UI_SOUND_IDS,
  UI_SOUND_MANIFEST,
  UiSoundBus,
  isUiSoundId,
  playUiSound,
  uiSoundBus,
  uiSoundFiles,
  unlockUiSounds,
  type UiSoundId,
  type UiSoundSpec,
} from './ui-sounds';
